import React, { createContext, useContext, useCallback, ReactNode } from 'react';
import { useAuth } from '../Contexts/AuthContext';
import useApiService from './ApiService';
import CONFIG from './CONFIG';
import {
  CreateConversationPayload,
  CreateDatasetPayload,
  EvalConversationDetail,
  EvalDataset,
  EvalDatasetDetail,
  EvalDatasetListResponse,
  SimulationDatasetApiContextValue,
  SuccessResponse,
  UpdateConversationPayload,
  UpdateDatasetPayload,
} from './SimulationDatasetApiProviderTypes';

const SimulationDatasetApiContext = createContext<SimulationDatasetApiContextValue | undefined>(
  undefined,
);

const BASE_PATH = '/v1/eval';

export const SimulationDatasetApiProvider = ({ children }: { children: ReactNode }) => {
  const { get, post, put, del } = useApiService(CONFIG.SERVER_ASSISTANT);
  const auth = useAuth();

  const getDatasets = useCallback(
    async (skip = 0, limit = 10): Promise<EvalDatasetListResponse> => {
      try {
        const response = await get(`${BASE_PATH}/datasets?skip=${skip}&limit=${limit}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching eval datasets:', error);
        throw error;
      }
    },
    [get, auth],
  );

  const createDataset = useCallback(
    async (body: CreateDatasetPayload): Promise<EvalDataset> => {
      try {
        const response = await post(`${BASE_PATH}/datasets`, body);
        return response.data;
      } catch (error) {
        console.error('Error creating eval dataset:', error);
        throw error;
      }
    },
    [post],
  );

  const getDataset = useCallback(
    async (datasetKey: string): Promise<EvalDatasetDetail> => {
      try {
        const response = await get(`${BASE_PATH}/datasets/${datasetKey}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching eval dataset detail:', error);
        throw error;
      }
    },
    [get],
  );

  const updateDataset = useCallback(
    async (datasetKey: string, body: UpdateDatasetPayload): Promise<EvalDataset> => {
      try {
        const response = await put(`${BASE_PATH}/datasets/${datasetKey}`, body);
        return response.data;
      } catch (error) {
        console.error('Error updating eval dataset:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteDataset = useCallback(
    async (datasetKey: string): Promise<SuccessResponse> => {
      try {
        const response = await del(`${BASE_PATH}/datasets/${datasetKey}`);
        return response.data;
      } catch (error) {
        console.error('Error deleting eval dataset:', error);
        throw error;
      }
    },
    [del],
  );

  const startEvaluation = useCallback(
    async (datasetKey: string, assistantKeys: string[]) => {
      try {
        const response = await post(`${BASE_PATH}/datasets/${datasetKey}/evaluations`, {
          assistant_keys: assistantKeys,
        });
        return response.data;
      } catch (error) {
        console.error('Error starting evaluation:', error);
        throw error;
      }
    },
    [post],
  );

  const getEvaluations = useCallback(
    async (datasetKey: string, skip = 0, limit = 10) => {
      try {
        const response = await get(
          `${BASE_PATH}/datasets/${datasetKey}/evaluations?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching evaluations:', error);
        throw error;
      }
    },
    [get],
  );

  const getEvaluationDetail = useCallback(
    async (datasetKey: string, runKey: string) => {
      try {
        const response = await get(`${BASE_PATH}/datasets/${datasetKey}/evaluations/${runKey}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching evaluation detail:', error);
        throw error;
      }
    },
    [get],
  );

  const getEvaluationScans = useCallback(
    async (datasetKey: string, runKey: string, skip = 0, limit = 10) => {
      try {
        const response = await get(
          `${BASE_PATH}/datasets/${datasetKey}/evaluations/${runKey}/scans?skip=${skip}&limit=${limit}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching evaluation scans:', error);
        throw error;
      }
    },
    [get],
  );

  const getScanDetail = useCallback(
    async (datasetKey: string, scanKey: string) => {
      try {
        const response = await get(`${BASE_PATH}/datasets/${datasetKey}/scans/${scanKey}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching scan detail:', error);
        throw error;
      }
    },
    [get],
  );

  const getConversations = useCallback(
    async (datasetKey: string, skip = 0, limit = 10, search?: string) => {
      try {
        const params = new URLSearchParams({ skip: String(skip), limit: String(limit) });
        if (search) params.set('search', search);
        const response = await get(
          `${BASE_PATH}/datasets/${datasetKey}/conversations?${params.toString()}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching conversations:', error);
        throw error;
      }
    },
    [get],
  );

  const createConversation = useCallback(
    async (
      datasetKey: string,
      body: CreateConversationPayload,
    ): Promise<EvalConversationDetail> => {
      try {
        const response = await post(`${BASE_PATH}/datasets/${datasetKey}/conversations`, body);
        return response.data;
      } catch (error) {
        console.error('Error creating eval conversation:', error);
        throw error;
      }
    },
    [post],
  );

  const importConversation = useCallback(
    async (datasetKey: string, thread: string): Promise<EvalConversationDetail> => {
      const response = await post(`${BASE_PATH}/datasets/${datasetKey}/conversations/import`, {
        thread,
      });
      return response.data;
    },
    [post],
  );

  const getConversation = useCallback(
    async (datasetKey: string, conversationKey: string): Promise<EvalConversationDetail> => {
      try {
        const response = await get(
          `${BASE_PATH}/datasets/${datasetKey}/conversations/${conversationKey}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error fetching eval conversation:', error);
        throw error;
      }
    },
    [get],
  );

  const updateConversation = useCallback(
    async (
      datasetKey: string,
      conversationKey: string,
      body: UpdateConversationPayload,
    ): Promise<EvalConversationDetail> => {
      try {
        const response = await put(
          `${BASE_PATH}/datasets/${datasetKey}/conversations/${conversationKey}`,
          body,
        );
        return response.data;
      } catch (error) {
        console.error('Error updating eval conversation:', error);
        throw error;
      }
    },
    [put],
  );

  const deleteConversation = useCallback(
    async (datasetKey: string, conversationKey: string): Promise<SuccessResponse> => {
      try {
        const response = await del(
          `${BASE_PATH}/datasets/${datasetKey}/conversations/${conversationKey}`,
        );
        return response.data;
      } catch (error) {
        console.error('Error deleting eval conversation:', error);
        throw error;
      }
    },
    [del],
  );

  return (
    <SimulationDatasetApiContext.Provider
      value={{
        getDatasets,
        createDataset,
        getDataset,
        updateDataset,
        deleteDataset,
        startEvaluation,
        getEvaluations,
        getEvaluationDetail,
        getEvaluationScans,
        getScanDetail,
        getConversations,
        createConversation,
        importConversation,
        getConversation,
        updateConversation,
        deleteConversation,
      }}
    >
      {children}
    </SimulationDatasetApiContext.Provider>
  );
};

export const useSimulationDatasetApi = (): SimulationDatasetApiContextValue => {
  const context = useContext(SimulationDatasetApiContext);
  if (!context) {
    throw new Error('useSimulationDatasetApi must be used within a SimulationDatasetApiProvider');
  }
  return context;
};
